import { useEffect, useRef, useState } from 'react';

/**
 * Detects if text content is truncated by CSS.
 * Requires the following CSS properties on the target element:
 * ```css
 * display: -webkit-box;
 * -webkit-box-orient: vertical;
 * -webkit-line-clamp: N;
 * overflow: hidden;
 * text-overflow: ellipsis;
 * ```
 * @param {string} content - Text content to monitor for truncation
 * @returns {{ ref: React.RefObject<HTMLDivElement>, isTruncated: boolean }}
 */
export const useTextTruncation = (content: string) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      if (!ref.current) {
        return;
      }
      const { scrollHeight, clientHeight } = ref.current;

      setIsTruncated(scrollHeight > clientHeight);
    });

    return () => cancelAnimationFrame(rafId);
  }, [content]);

  return { ref, isTruncated };
};
